import { makeStyles } from "@material-ui/core";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  usePlByProgramStyles,
  PlPageHeader,
  PL_FONT,
  PL_TH_BG,
  PL_TH_TEXT,
  PL_TH_BORDER,
  PL_POS_C,
  PL_NEG_C,
} from "./plByProgramTheme";
import { useReportFonts } from "../../reportFonts";
import { getCmActualsVsForecastAPI } from "../../../../api/reports-api";
import { toReportApiFilters } from "../filterRelationshipUtils";
import { getApiFilterKey, getGlobalPeriodHiddenKeys, resolvePeriodCellValue, formatActualsThroughDate, formatActualsThroughFromLabel, headerActualsDate } from "./plTimeFilterUtils";
import { applyNumFilter, isNumFilterActive, calcOpDropdownPos, renderStandardNumFilter, renderStandardTextFilter } from "./plTableFilterUtils";
import { OpDropdownContent } from "../waterfall-eac/tableColumnFilterShared";
import { calcFilterPos, FILTER_PANEL_WIDTH_NUMERIC } from "../waterfall-eac/filterPopoverUtils";
import Loading from "../../../../common-components/Loading";
import CmActualsColumnPicker from "./CmActualsColumnPicker";
import { TH_ACTUAL_BG, TH_ACTUAL_TEXT } from "../../reportTableConfig";

const HAIRLINE     = "#E2E8F0";
const HEADER_TOP   = PL_TH_BG;
const HEADER_ROW   = PL_TH_BG;
const HEADER_LABEL = PL_TH_TEXT;
const CELL_TEXT    = "#374151";
const TEXT_MUTED   = "#6B7280";
const ROW_STRIPE   = "#F7FAFC";
const ACTUAL_TH_BG = TH_ACTUAL_BG;
const ACTUAL_TH_FG = TH_ACTUAL_TEXT;

/* ══════════════════════════════════════════════════════════════ */
/*  Column & row metadata                                         */
/* ══════════════════════════════════════════════════════════════ */
const COLS = [
  { key: "jan",   label: "Jan", isYtd: false, isTotal: false },
  { key: "feb",   label: "Feb", isYtd: false, isTotal: false },
  { key: "mar",   label: "Mar", isYtd: false, isTotal: false },
  { key: "apr",   label: "Apr", isYtd: false, isTotal: false },
  { key: "may",   label: "May", isYtd: false, isTotal: false },
  { key: "jun",   label: "Jun", isYtd: false, isTotal: false },
  { key: "jul",   label: "Jul", isYtd: false, isTotal: false },
  { key: "aug",   label: "Aug", isYtd: false, isTotal: false },
  { key: "sep",   label: "Sep", isYtd: false, isTotal: false },
  { key: "oct",   label: "Oct", isYtd: false, isTotal: false },
  { key: "nov",   label: "Nov", isYtd: false, isTotal: false },
  { key: "dec",   label: "Dec", isYtd: false, isTotal: false },
  { key: "ytd",   label: "YTD Actuals", isYtd: true,  isTotal: false },
  { key: "total", label: "Total Year",  isYtd: false, isTotal: true  },
];

const ROW_DEFS = [
  { key: "contractRevenue",  label: "Revenue",  type: "subtotal", isPct: false },
  { key: "directLabor",      label: "Direct Labor",      type: "data",     isPct: false },
  { key: "subcontractor",    label: "Subcontractor",     type: "data",     isPct: false },
  { key: "odcs",             label: "ODCs",              type: "data",     isPct: false },
  { key: "totalDirectCost",  label: "Total Direct Cost", type: "subtotal", isPct: false },
  { key: "grossMargin",      label: "Gross Margin",      type: "metric",   isPct: false },
  { key: "grossMarginPct",   label: "Gross Margin %",    type: "pct",      isPct: true  },
  { key: "indirectCost",     label: "Indirect Cost",     type: "data",     isPct: false },
  { key: "totalCost",        label: "Total Cost",        type: "data",     isPct: false },
  { key: "netProfit",        label: "Net Profit / Fee",  type: "data",     isPct: false },
  { key: "netMarginPct",     label: "Net Margin %",      type: "pct",      isPct: true  },
];

/* ══════════════════════════════════════════════════════════════ */
/*  Number formatters                                             */
/* ══════════════════════════════════════════════════════════════ */
const fmtAbs = (v) => Math.abs(v).toLocaleString("en-US", { maximumFractionDigits: 0 });

function fmtCell(v, isPct) {
  if (isPct) return `${v.toFixed(1)}%`;
  if (v === 0) return "—";
  return v < 0 ? `($${fmtAbs(v)})` : `$${fmtAbs(v)}`;
}

function fmtVariance(v, isPct) {
  const n = Number(v);
  const val = Number.isFinite(n) ? n : 0;
  if (isPct) {
    if (val === 0) return "0.0%";
    return val > 0 ? `${val.toFixed(1)}%` : `(${Math.abs(val).toFixed(1)}%)`;
  }
  if (val === 0) return "$0";
  return val > 0 ? `$${fmtAbs(val)}` : `($${fmtAbs(val)})`;
}

const varColor = (v) => (v === 0 || v == null ? CELL_TEXT : v > 0 ? PL_POS_C : PL_NEG_C);

/* ══════════════════════════════════════════════════════════════ */
/*  Column filter helpers — see plTableFilterUtils.js              */
/* ══════════════════════════════════════════════════════════════ */

/* ══════════════════════════════════════════════════════════════ */
/*  Row visual config                                             */
/* ══════════════════════════════════════════════════════════════ */
const ROW_STYLE = {
  data:     { labelColor: CELL_TEXT, fontWeight: 400 },
  subtotal: { labelColor: CELL_TEXT, fontWeight: 700 },
  metric:   { labelColor: CELL_TEXT, fontWeight: 700 },
  pct:      { labelColor: CELL_TEXT, fontWeight: 400 },
};

/* ══════════════════════════════════════════════════════════════ */
/*  Styles                                                        */
/* ══════════════════════════════════════════════════════════════ */
const useStyles = makeStyles(() => ({
  tableSection: {
    marginBottom: 24,
    background: "#FFFFFF",
    borderRadius: 12,
    border: "1px solid #EBEBEB",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
    overflow: "visible",
  },
  tableSectionHead: {
    padding: "16px 20px 14px 24px",
    borderBottom: `1px solid ${HAIRLINE}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    position: "relative",
    zIndex: 20,
    overflow: "visible",
  },
  tableSectionHeadText: { minWidth: 0 },
  tableSectionTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: CELL_TEXT,
    letterSpacing: "-0.02em",
    lineHeight: 1.35,
    fontFamily: PL_FONT,
    margin: 0,
  },
  tableSectionMeta: {
    marginTop: 4,
    fontSize: 12,
    fontWeight: 400,
    color: TEXT_MUTED,
    fontFamily: PL_FONT,
    lineHeight: 1.4,
  },
  colPickerToolbar: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: 12,
    position: "relative",
    zIndex: 30,
  },
  tableWrap: {
    overflowX: "auto",
    overflowY: "hidden",
    background: "#FFFFFF",
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontFamily: PL_FONT,
    fontSize: 13,
    tableLayout: "auto",
  },
  thead: {
    position: "sticky",
    top: 0,
    zIndex: 2,
  },
  thMetric: {
    background: HEADER_TOP, color: HEADER_LABEL,
    padding: "14px 20px", fontWeight: 600, fontSize: 10,
    textAlign: "left", whiteSpace: "nowrap", minWidth: 180,
    position: "sticky", left: 0, zIndex: 4,
    letterSpacing: "0.08em", textTransform: "uppercase", verticalAlign: "middle",
    borderBottom: `1px solid ${PL_TH_BORDER}`, borderRight: `1px solid ${PL_TH_BORDER}`,
  },
  thBandActuals: {
    background: HEADER_TOP, color: HEADER_LABEL,
    padding: "12px 10px", fontWeight: 600, fontSize: 10,
    textAlign: "center", whiteSpace: "nowrap", letterSpacing: "0.06em", textTransform: "uppercase",
    borderBottom: `1px solid ${PL_TH_BORDER}`, borderRight: `1px solid ${PL_TH_BORDER}`,
  },
  thBandForecast: {
    background: HEADER_TOP, color: HEADER_LABEL,
    padding: "12px 10px", fontWeight: 600, fontSize: 10,
    textAlign: "center", whiteSpace: "nowrap", letterSpacing: "0.06em", textTransform: "uppercase",
    borderBottom: `1px solid ${PL_TH_BORDER}`, borderRight: `1px solid ${PL_TH_BORDER}`,
  },
  thBandYtd: {
    background: HEADER_TOP, color: HEADER_LABEL,
    padding: "12px 10px", fontWeight: 600, fontSize: 10,
    textAlign: "center", whiteSpace: "nowrap", letterSpacing: "0.06em", textTransform: "uppercase",
    minWidth: 88, verticalAlign: "middle",
    borderBottom: `1px solid ${PL_TH_BORDER}`, borderRight: `1px solid ${PL_TH_BORDER}`,
  },
  thBandTotal: {
    background: HEADER_TOP, color: HEADER_LABEL,
    padding: "12px 10px", fontWeight: 600, fontSize: 10,
    textAlign: "center", whiteSpace: "nowrap", letterSpacing: "0.06em", textTransform: "uppercase",
    minWidth: 88, verticalAlign: "middle",
    borderBottom: `1px solid ${PL_TH_BORDER}`, borderRight: `1px solid ${PL_TH_BORDER}`,
  },
  thMonthActual: {
    background: ACTUAL_TH_BG, color: ACTUAL_TH_FG,
    padding: "12px 10px", fontWeight: 500, fontSize: 12,
    textAlign: "right", minWidth: 72, whiteSpace: "nowrap",
    borderRight: `1px solid ${PL_TH_BORDER}`,
    "& $filterIconBtn": {
      color: ACTUAL_TH_FG,
      opacity: 0.65,
      "&:hover": { opacity: 1, background: "rgba(0,0,0,0.08)" },
    },
    "& $filterIconBtnActive": {
      opacity: 1,
      color: ACTUAL_TH_FG,
      background: "rgba(0,0,0,0.12)",
    },
  },
  thMonthForecast: {
    background: HEADER_ROW, color: HEADER_LABEL,
    padding: "12px 10px", fontWeight: 500, fontSize: 12,
    textAlign: "right", minWidth: 72, whiteSpace: "nowrap",
    borderRight: `1px solid ${PL_TH_BORDER}`,
  },
  thColYtd: {
    background: HEADER_ROW, color: HEADER_LABEL,
    padding: "12px 10px", fontWeight: 600, fontSize: 12,
    textAlign: "right", minWidth: 88, whiteSpace: "nowrap",
    borderRight: `1px solid ${PL_TH_BORDER}`,
  },
  thColTotal: {
    background: HEADER_ROW, color: HEADER_LABEL,
    padding: "12px 10px", fontWeight: 600, fontSize: 12,
    textAlign: "right", minWidth: 88, whiteSpace: "nowrap",
    borderRight: `1px solid ${PL_TH_BORDER}`,
  },
  trRow: {
    transition: "background-color 0.12s ease",
  },
  trStripe: {
    "& td": { background: ROW_STRIPE },
    "& td:first-child": { background: ROW_STRIPE },
  },
  tdLabel: {
    padding: "13px 20px",
    whiteSpace: "nowrap",
    borderBottom: `1px solid ${HAIRLINE}`,
    fontSize: 13,
    position: "sticky",
    left: 0,
    zIndex: 1,
    background: "#FFFFFF",
    boxShadow: "6px 0 12px -8px rgba(0,0,0,0.06)",
    textTransform: "capitalize",
  },
  tdActual: {
    padding: "13px 14px",
    textAlign: "right",
    color: CELL_TEXT,
    borderBottom: `1px solid ${HAIRLINE}`,
    fontSize: 13,
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
    background: "#FFFFFF",
  },
  tdForecast: {
    padding: "13px 14px",
    textAlign: "right",
    color: CELL_TEXT,
    borderBottom: `1px solid ${HAIRLINE}`,
    fontSize: 13,
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
    background: "#FFFFFF",
  },
  tdYtd: {
    padding: "13px 14px",
    textAlign: "right",
    color: CELL_TEXT,
    borderBottom: `1px solid ${HAIRLINE}`,
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
    background: "#FFFFFF",
  },
  tdTotal: {
    padding: "13px 14px",
    textAlign: "right",
    color: CELL_TEXT,
    borderBottom: `1px solid ${HAIRLINE}`,
    fontSize: 13,
    fontWeight: 700,
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
    background: "#FFFFFF",
  },
  tdVariance: {
    padding: "13px 14px",
    textAlign: "right",
    color: CELL_TEXT,
    borderBottom: `1px solid ${HAIRLINE}`,
    fontSize: 13,
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
    background: "#FFFFFF",
  },

  /* ── floating filter row ── */
  trFilter: {
    background: "#F1F5F9",
    borderBottom: `2px solid ${HAIRLINE}`,
  },
  filterLabelCell: {
    padding: "5px 10px",
    position: "sticky",
    left: 0,
    zIndex: 3,
    background: "#F1F5F9",
    boxShadow: "6px 0 12px -8px rgba(0,0,0,0.06)",
  },
  filterNumCell: {
    padding: "5px 6px",
    background: "#F1F5F9",
  },
  filterWrap: {
    display: "flex",
    alignItems: "center",
    gap: 3,
    height: 26,
  },
  filterTextInput: {
    flex: 1,
    height: 26,
    border: "1.5px solid #CBD5E1",
    borderRadius: 5,
    fontSize: 11,
    padding: "0 7px",
    outline: "none",
    fontFamily: PL_FONT,
    background: "#fff",
    color: "#111827",
    "&:focus": { borderColor: "#4F46E5" },
    "&::placeholder": { color: "#9CA3AF" },
  },
  filterNumInput: {
    width: "100%",
    height: 26,
    border: "1.5px solid #CBD5E1",
    borderRadius: 5,
    fontSize: 11,
    padding: "0 5px",
    outline: "none",
    fontFamily: PL_FONT,
    background: "#fff",
    color: "#111827",
    textAlign: "right",
    "&:focus": { borderColor: "#4F46E5" },
    "&::placeholder": { color: "#CBD5E1" },
  },
  filterOpBtn: {
    flexShrink: 0,
    width: 22,
    height: 26,
    border: "1.5px solid #CBD5E1",
    borderRadius: 5,
    background: "#fff",
    fontSize: 11,
    fontWeight: 700,
    color: "#4F46E5",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    fontFamily: PL_FONT,
    "&:hover": { background: "#EEF2FF", borderColor: "#4F46E5" },
  },
  filterOpBtnActive: {
    background: "#4F46E5",
    borderColor: "#4F46E5",
    color: "#fff",
    "&:hover": { background: "#4338CA" },
  },
  filterClearBtn: {
    flexShrink: 0,
    width: 18,
    height: 18,
    border: "none",
    borderRadius: "50%",
    background: "#E2E8F0",
    fontSize: 9,
    color: "#64748B",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    "&:hover": { background: "#CBD5E1" },
  },

  /* operator dropdown (rendered via portal) */
  opDropdown: {
    position: "fixed",
    zIndex: 100,
    background: "#fff",
    border: "1px solid #E2E8F0",
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    minWidth: 190,
    overflow: "hidden",
    fontFamily: PL_FONT,
  },
  opDropdownItem: {
    display: "block",
    width: "100%",
    padding: "8px 14px",
    fontSize: 12,
    color: "#374151",
    background: "none",
    border: "none",
    textAlign: "left",
    cursor: "pointer",
    fontFamily: PL_FONT,
    "&:hover": { background: "#EEF2FF", color: "#4F46E5" },
  },
  opDropdownItemActive: {
    background: "#EEF2FF",
    color: "#4F46E5",
    fontWeight: 600,
  },

  /* ── column picker ── */
  colPickerWrap: {
    position: "relative",
    flexShrink: 0,
  },
  colsBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 30,
    padding: "0 12px",
    borderRadius: 6,
    border: "1px solid #E2E8F0",
    background: "#fff",
    fontSize: 12,
    fontWeight: 500,
    color: "#374151",
    cursor: "pointer",
    fontFamily: PL_FONT,
    transition: "border-color 0.15s, background 0.15s",
    "&:hover": { background: "#F9FAFB", borderColor: "#C7D2FE" },
  },
  colsBtnActive: { borderColor: "#C7D2FE", background: "#EEF2FF", color: "#4F46E5" },
  hiddenBadge: {
    display: "inline-flex",
    alignItems: "center",
    height: 18,
    padding: "0 7px",
    borderRadius: 10,
    background: "#4F46E5",
    color: "#fff",
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1,
  },
  colPicker: {
    position: "absolute",
    top: "calc(100% + 4px)",
    right: 16,
    zIndex: 1200,
    background: "#fff",
    border: "1px solid #E2E8F0",
    borderRadius: 10,
    boxShadow: "0 8px 28px rgba(0,0,0,0.12)",
    minWidth: 180,
    maxHeight: 420,
    overflowY: "auto",
    fontFamily: PL_FONT,
  },
  colPickerHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 14px 8px",
    borderBottom: "1px solid #F3F4F6",
  },
  colPickerTitle: { fontSize: 11, fontWeight: 700, color: "#6B7280", letterSpacing: "0.05em", textTransform: "uppercase" },
  colPickerReset: {
    fontSize: 11,
    fontWeight: 600,
    color: "#4F46E5",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    fontFamily: PL_FONT,
    "&:hover": { textDecoration: "underline" },
  },
  colPickerItem: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    padding: "7px 14px",
    cursor: "pointer",
    fontSize: 12,
    color: "#374151",
    userSelect: "none",
    borderBottom: "1px solid #F9FAFB",
    "&:last-child": { borderBottom: "none" },
    "&:hover": { background: "#F9FAFB" },
  },
  colPickerCheck: {
    width: 14,
    height: 14,
    accentColor: "#4F46E5",
    cursor: "pointer",
    flexShrink: 0,
  },
  filterIconBtn: {
    flexShrink: 0, width: 16, height: 16, border: "none",
    background: "transparent", cursor: "pointer", padding: 0,
    borderRadius: 3, color: PL_TH_TEXT, opacity: 0.5,
    display: "flex", alignItems: "center", justifyContent: "center",
    "&:hover": { opacity: 1, background: "rgba(255,255,255,0.14)" },
  },
  filterIconBtnActive: {
    opacity: 1, color: "#A5B4FC",
    background: "rgba(165,180,252,0.18)", borderRadius: 3,
  },
  filterPanel: {
    position: "fixed", zIndex: 99, background: "#fff",
    border: "1px solid #E2E8F0", borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    padding: "10px 12px", minWidth: 180, fontFamily: PL_FONT,
  },
}));

/* ══════════════════════════════════════════════════════════════ */
/*  Helpers                                                       */
/* ══════════════════════════════════════════════════════════════ */

// API returns pct rows as ratios (0.3771 = 37.71%); multiply by 100 for display.
function normalizePct(dataset) {
  const out = { ...dataset };
  ROW_DEFS.forEach(({ key, isPct }) => {
    if (isPct && out[key]) {
      const transformed = {};
      COLS.forEach(({ key: ck }) => {
        transformed[ck] = (out[key][ck] ?? 0) * 100;
      });
      out[key] = transformed;
    }
  });
  return out;
}

function computeVariance(live, comparison) {
  const out = {};
  ROW_DEFS.forEach(({ key }) => {
    out[key] = {};
    COLS.forEach(({ key: ck }) => {
      out[key][ck] = (live[key]?.[ck] ?? 0) - (comparison[key]?.[ck] ?? 0);
    });
  });
  return out;
}

function FilterFunnelIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  DataTable component                                           */
/* ══════════════════════════════════════════════════════════════ */
function DataTable({ data, isVariance, classes, actualCols, year, wrapRef, onScroll, title, meta, hiddenCols, globalHiddenCols, filters, varianceSources }) {
  /* ── all state at the top ── */
  const [labelFilter,   setLabelFilter]   = useState("");
  const [colFilters,    setColFilters]    = useState({});
  const [openOpKey,     setOpenOpKey]     = useState(null);
  const [dropdownPos,   setDropdownPos]   = useState({ top: 0, left: 0 });
  const opBtnRefs        = useRef({});
  const [openFilterKey,  setOpenFilterKey]  = useState(null);
  const [filterPanelPos, setFilterPanelPos] = useState({ top: 0, left: 0 });
  const filterBtnRefs    = useRef({});
  const openFilterKeyRef = useRef(null);
  const openOpKeyRef     = useRef(null);

  useEffect(() => { openFilterKeyRef.current = openFilterKey; }, [openFilterKey]);
  useEffect(() => { openOpKeyRef.current     = openOpKey;     }, [openOpKey]);

  /* Reposition filter panel and op dropdown to follow header on any scroll */
  useEffect(() => {
    const reposition = () => {
      const key = openFilterKeyRef.current;
      if (key) {
        const btn = filterBtnRefs.current[key];
        if (btn) setFilterPanelPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
      }
      const opKey = openOpKeyRef.current;
      if (opKey) {
        const opBtn = opBtnRefs.current[opKey];
        if (opBtn) setDropdownPos(calcOpDropdownPos(opBtn));
      }
    };
    window.addEventListener("scroll", reposition, true);
    return () => window.removeEventListener("scroll", reposition, true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── column group presence (data-driven) ── */
  const monthCols    = COLS.slice(0, 12);
  const hasActuals   = monthCols.some((c) => actualCols.has(c.key));
  const hasForecasts = monthCols.some((c) => !actualCols.has(c.key));
  const showYtd      = hasActuals;

  /* columns that have data (before user hide) */
  const dataMonthCols = monthCols.filter((c) =>
    actualCols.has(c.key) ? hasActuals : hasForecasts
  );
  const dataCols = COLS.filter((col) => {
    if (col.isTotal) return true;
    if (col.isYtd)   return showYtd;
    return actualCols.has(col.key) ? hasActuals : hasForecasts;
  });

  /* apply user hide + global time filter — a selected global month/quarter
     filter overrides the table-level picker (e.g. hiding Jan via the picker
     then selecting Jan globally must bring the Jan column back). */
  const isColHidden = (key) => {
    if (globalHiddenCols?.has(key)) return true;
    if (globalHiddenCols?.size > 0) return false;
    return hiddenCols.has(key);
  };
  const visibleMonthCols = dataMonthCols.filter((c) => !isColHidden(c.key));
  const visibleCols      = dataCols.filter((c) => !isColHidden(c.key));

  const bandActualCount   = visibleMonthCols.filter((c) => actualCols.has(c.key)).length;
  const bandForecastCount = visibleMonthCols.filter((c) => !actualCols.has(c.key)).length;
  const showYtdDisplay    = showYtd && !isColHidden("ytd");

  const updateColFilter = useCallback((colKey, field, value) =>
    setColFilters((prev) => ({
      ...prev,
      [colKey]: { op: "gt", ...prev[colKey], [field]: value },
    })), []);

  const clearColFilter = useCallback((colKey) =>
    setColFilters((prev) => { const n = { ...prev }; delete n[colKey]; return n; }), []);

  const isColFilterActive = (colKey) => isNumFilterActive(colFilters[colKey]);

  const toggleOpDropdown = (colKey) => {
    if (openOpKey === colKey) { setOpenOpKey(null); return; }
    const btn = opBtnRefs.current[colKey];
    if (btn) setDropdownPos(calcOpDropdownPos(btn));
    setOpenOpKey(colKey);
  };

  const selectOp = (colKey, opKey) => {
    updateColFilter(colKey, "op", opKey);
    setOpenOpKey(null);
  };

  /* Close dropdown on outside click */
  useEffect(() => {
    if (!openOpKey) return;
    const handler = (e) => {
      if (!e.target.closest("[data-op-dd]") && !e.target.closest("[data-op-dropdown]") && !e.target.closest("[data-op-btn]"))
        setOpenOpKey(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openOpKey]);

  useEffect(() => {
    if (!openFilterKey) return;
    const h = (e) => {
      if (!e.target.closest("[data-filter-panel]") && !e.target.closest("[data-filter-btn]") && !e.target.closest("[data-op-dd]") && !e.target.closest("[data-op-dropdown]"))
        setOpenFilterKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openFilterKey]);

  const cellOpts = (row) => ({
    rowKey: row.key,
    allRowData: data,
    isPct: row.isPct,
    varianceSources: isVariance ? varianceSources : null,
    actualMonthKeys: actualCols,
  });

  const rowPasses = (row) => {
    if (labelFilter && !row.label.toLowerCase().includes(labelFilter.toLowerCase())) return false;
    for (const [colKey, filter] of Object.entries(colFilters)) {
      if (!isColFilterActive(colKey)) continue;
      const rawVal = resolvePeriodCellValue(data[row.key], colKey, filters, "Month", cellOpts(row));
      if (!applyNumFilter(rawVal, filter)) return false;
    }
    return true;
  };

  /* ── shared cell helpers ── */
  const cellClass = (col) => {
    if (col.isTotal) return classes.tdTotal;
    if (isVariance) return classes.tdVariance;
    if (col.isYtd) return classes.tdYtd;
    if (actualCols.has(col.key)) return classes.tdActual;
    return classes.tdForecast;
  };
  const rowClass   = (row, idx) => {
    return idx % 2 === 1 ? classes.trStripe : "";
  };
  const cellWeight = (row) => {
    if (row.type === "subtotal" || row.type === "metric") return 700;
    return ROW_STYLE[row.type].fontWeight;
  };

  const getColLabel = (colKey) => COLS.find((c) => c.key === colKey)?.label || colKey;

  const renderNumFilterDiv = (colKey) => renderStandardNumFilter({
    label: getColLabel(colKey),
    colKey,
    colFilters,
    updateColFilter,
    clearColFilter,
    toggleOpDropdown,
    opBtnRefs,
  });

  const hasFilterValue = (key) => {
    if (key === "label") return !!labelFilter;
    return isColFilterActive(key);
  };

  const renderFilterContent = (key) => {
    if (key === "label") return renderStandardTextFilter({ label: "Metric", value: labelFilter, onChange: setLabelFilter, onClear: () => setLabelFilter(""), placeholder: "Search metrics…" });
    return renderNumFilterDiv(key);
  };

  const toggleFilter = (key) => {
    if (openFilterKey === key) { setOpenFilterKey(null); return; }
    const btn = filterBtnRefs.current[key];
    if (btn) setFilterPanelPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
    setOpenFilterKey(key);
    setOpenOpKey(null);
  };

  return (
    <>
      {/* ── Section head: title + Columns button inline ── */}
      <div className={classes.tableSectionHead}>
        <div className={classes.tableSectionHeadText}>
          <h2 className={classes.tableSectionTitle}>{title}</h2>
          {meta && <div className={classes.tableSectionMeta}>{meta}</div>}
        </div>
      </div>

    <div className={classes.tableWrap} ref={wrapRef} onScroll={onScroll}>
      <table className={classes.table}>
        <thead className={classes.thead}>
          {/* ── Band row ── */}
          <tr>
            <th className={classes.thMetric} rowSpan={2}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
                <span style={{ flex: 1, color: openFilterKey === "label" ? "#60A5FA" : undefined }}>Metric</span>
                <button ref={(el) => { filterBtnRefs.current["label"] = el; }} data-filter-btn
                  className={`${classes.filterIconBtn} ${hasFilterValue("label") ? classes.filterIconBtnActive : ""}`}
                  onClick={() => toggleFilter("label")} title="Filter">
                  <FilterFunnelIcon />
                </button>
              </div>
            </th>
            {isVariance ? (
              (bandActualCount + bandForecastCount) > 0 &&
              <th className={classes.thBandActuals} colSpan={bandActualCount + bandForecastCount}>{year}</th>
            ) : (
              <>
                {bandActualCount   > 0 && <th className={classes.thBandActuals}  colSpan={bandActualCount}>{year} — Actuals</th>}
                {bandForecastCount > 0 && <th className={classes.thBandForecast} colSpan={bandForecastCount}>{year} — Forecast</th>}
              </>
            )}
            {showYtdDisplay        && <th className={classes.thBandYtd}>Actuals</th>}
            {!isColHidden("total") && <th className={classes.thBandTotal}>Total Year</th>}
          </tr>
          {/* ── Month label row ── */}
          <tr>
            {visibleMonthCols.map((c) => (
              <th key={c.key} className={actualCols.has(c.key) ? classes.thMonthActual : classes.thMonthForecast}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                  <span style={{ color: actualCols.has(c.key) ? ACTUAL_TH_FG : openFilterKey === c.key ? "#60A5FA" : undefined }}>{c.label}</span>
                  <button ref={(el) => { filterBtnRefs.current[c.key] = el; }} data-filter-btn
                    className={`${classes.filterIconBtn} ${hasFilterValue(c.key) ? classes.filterIconBtnActive : ""}`}
                    onClick={() => toggleFilter(c.key)} title="Filter">
                    <FilterFunnelIcon />
                  </button>
                </div>
              </th>
            ))}
            {showYtdDisplay && (
              <th className={classes.thColYtd}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                  <span style={{ color: openFilterKey === "ytd" ? "#60A5FA" : undefined }}>YTD</span>
                  <button ref={(el) => { filterBtnRefs.current["ytd"] = el; }} data-filter-btn
                    className={`${classes.filterIconBtn} ${hasFilterValue("ytd") ? classes.filterIconBtnActive : ""}`}
                    onClick={() => toggleFilter("ytd")} title="Filter">
                    <FilterFunnelIcon />
                  </button>
                </div>
              </th>
            )}
            {!isColHidden("total") && (
              <th className={classes.thColTotal}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                  <span style={{ color: openFilterKey === "total" ? "#60A5FA" : undefined }}>Year</span>
                  <button ref={(el) => { filterBtnRefs.current["total"] = el; }} data-filter-btn
                    className={`${classes.filterIconBtn} ${hasFilterValue("total") ? classes.filterIconBtnActive : ""}`}
                    onClick={() => toggleFilter("total")} title="Filter">
                    <FilterFunnelIcon />
                  </button>
                </div>
              </th>
            )}
          </tr>
        </thead>

        <tbody>
          {ROW_DEFS.map((row, idx) => {
            const rs = ROW_STYLE[row.type];
            const labelColor = rs.labelColor;
            const visible = rowPasses(row);

            return (
              <tr
                key={row.key}
                className={`${classes.trRow} ${rowClass(row, idx)}`}
                style={visible ? {} : { display: "none" }}
              >
                <td className={classes.tdLabel} style={{ color: labelColor, fontWeight: rs.fontWeight }}>
                  {row.label}
                </td>
                {visibleCols.map((col) => {
                  const rawVal = resolvePeriodCellValue(data[row.key], col.key, filters, "Month", cellOpts(row));
                  const text   = isVariance ? fmtVariance(rawVal, row.isPct) : fmtCell(rawVal, row.isPct);
                  const weight = cellWeight(row);
                  return (
                    <td
                      key={col.key}
                      className={cellClass(col)}
                      style={{ fontWeight: weight, ...(isVariance ? { color: varColor(rawVal) } : {}) }}
                    >
                      {text}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Operator dropdown — rendered at body level to escape overflow clipping */}
      {openOpKey && createPortal(
        <OpDropdownContent
          activeOp={(colFilters[openOpKey]?.op) || "gt"}
          onSelect={(op) => selectOp(openOpKey, op)}
          style={{ top: dropdownPos.top, left: dropdownPos.left }}
        />,
        document.body
      )}
      {openFilterKey && createPortal(
        <div data-filter-panel className={classes.filterPanel} style={{ top: filterPanelPos.top, left: filterPanelPos.left }}>
          {renderFilterContent(openFilterKey)}
        </div>,
        document.body
      )}
    </div>
    </>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Main export                                                   */
/* ══════════════════════════════════════════════════════════════ */
function buildApiFilters(filters) {
  return toReportApiFilters(filters, [
    "year", "orgId", "contractType", "contractId", "clinLvl2", "businessType", "awardType",
    "competitionType", "bu", "popEndDate", "sector", "projectId",
    "contractName", "customerName",
  ]);
}

export default function CurrentMonthActualsVsForecast({ filters, actualsThroughDate }) {
  const plClasses = usePlByProgramStyles();
  const localClasses = useStyles();
  useReportFonts();
  const classes = { ...plClasses, ...localClasses };

  const [live, setLive]                                         = useState(null);
  const [comparison, setComparison]                             = useState(null);
  const [liveVersion, setLiveVersion]                           = useState(null);
  const [comparisonVersion, setComparisonVersion]               = useState(null);
  const [actualCols, setActualCols]                             = useState(new Set());
  const [comparisonActualCols, setComparisonActualCols]         = useState(new Set());
  const [actualThroughMonth, setActualThroughMonth]             = useState(null);
  const [comparisonActualThroughMonth, setComparisonActualThroughMonth] = useState(null);
  const [loading, setLoading]                                   = useState(true);
  const [error, setError]                                       = useState(null);
  const [hiddenCols, setHiddenCols]                             = useState(() => new Set());

  const toggleCol = useCallback((key) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const showAllCols = useCallback(() => setHiddenCols(new Set()), []);

  const apiFilterKey = useMemo(() => getApiFilterKey(filters, buildApiFilters), [filters]);
  const globalPeriodHidden = useMemo(
    () => getGlobalPeriodHiddenKeys(filters, "Month", COLS.map((c) => c.key)),
    [filters?.monthName, filters?.quarter],
  );

  useEffect(() => {
    setLoading(true);
    setError(null);
    getCmActualsVsForecastAPI(buildApiFilters(filters))
      .then((res) => {
        if (res?.status === 200 && res.data) {
          const {
            live: liveRaw,
            comparison: comparisonRaw,
            actualColumns,
            comparisonActualColumns,
            actualThroughMonth: atm,
            comparisonActualThroughMonth: catm,
            liveVersion: lv,
            comparisonVersion: cv,
          } = res.data;
          setLive(normalizePct(liveRaw));
          setComparison(normalizePct(comparisonRaw));
          setActualCols(
            new Set(
              actualColumns?.length
                ? actualColumns
                : COLS.filter((c) => !c.isYtd && !c.isTotal)
                    .slice(0, Number(atm) || 0)
                    .map((c) => c.key),
            ),
          );
          /* comparisonActualThroughMonth is authoritative for the second/comparison
             table — no fallback to the live table's actualColumns/actualThroughMonth,
             since a 0/empty value there means that version has no actuals. */
          setComparisonActualCols(
            new Set(
              comparisonActualColumns?.length
                ? comparisonActualColumns
                : COLS.filter((c) => !c.isYtd && !c.isTotal)
                    .slice(0, Number(catm) || 0)
                    .map((c) => c.key),
            ),
          );
          setActualThroughMonth(atm ?? null);
          setComparisonActualThroughMonth(catm ?? null);
          setLiveVersion(lv ?? null);
          setComparisonVersion(cv ?? null);
        } else {
          setError(res?.message || "Failed to load data");
        }
      })
      .catch(() => setError("Failed to load data"))
      .finally(() => setLoading(false));
  }, [apiFilterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const variance = live && comparison ? computeVariance(live, comparison) : null;

  /* ── synchronized horizontal scroll ── */
  const wrapRefs  = useRef([null, null, null]);
  const isSyncing = useRef(false);
  const handleScroll = useCallback((srcIdx, e) => {
    if (isSyncing.current) return;
    isSyncing.current = true;
    const { scrollLeft } = e.target;
    wrapRefs.current.forEach((el, i) => {
      if (i !== srcIdx && el) el.scrollLeft = scrollLeft;
    });
    requestAnimationFrame(() => { isSyncing.current = false; });
  }, []);

  const liveLabel       = liveVersion?.name       ?? "Live Forecast";
  const comparisonLabel = comparisonVersion?.name ?? "Comparison";
  const year            = filters?.year && filters.year !== "All" ? filters.year : String(new Date().getFullYear());
  const liveYear        = String(new Date().getFullYear());

  const lastActualCol = [...COLS.filter((c) => !c.isYtd && !c.isTotal)].reverse().find((c) => actualCols.has(c.key));
  const actualThrough = lastActualCol ? lastActualCol.label : null;
  const headerDate = headerActualsDate(
    filters?.year,
    loading,
    formatActualsThroughDate(actualThroughMonth),
    formatActualsThroughFromLabel(actualThrough),
  );

  if (loading) return <Loading />;

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#DC2626", fontFamily: PL_FONT, fontSize: 14 }}>
        {error}
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        {/* ── Page header ── */}
        <PlPageHeader
          classes={classes}
          title="Current Month Actuals Vs Forecast"
          accent="Live Forecast"
          meta="Monthly P&L Actuals and Forecast"
          metaTop={actualThrough ? `Actuals through ${actualThrough} – ${liveYear}` : `FY-${year}`}
          date={headerDate}
        />

        <div className={classes.colPickerToolbar}>
          <CmActualsColumnPicker
            classes={classes}
            columns={COLS}
            hiddenCols={hiddenCols}
            globalHiddenCols={globalPeriodHidden}
            onToggleCol={toggleCol}
            onShowAllCols={showAllCols}
          />
        </div>

        <div className={classes.tableSection}>
          <DataTable data={live} isVariance={false} classes={classes} actualCols={actualCols} year={year}
            title={`Current Month Actuals + ${liveLabel}`}
            meta={`FY-${year} · Jan–Dec Monthly Breakdown`}
            hiddenCols={hiddenCols}
            globalHiddenCols={globalPeriodHidden}
            filters={filters}
            wrapRef={(el) => { wrapRefs.current[0] = el; }} onScroll={(e) => handleScroll(0, e)} />
        </div>

        <div className={classes.tableSection}>
          <DataTable data={comparison} isVariance={false} classes={classes} actualCols={comparisonActualCols} year={year}
            title={`Current Month Actuals + ${comparisonLabel} Forecast`}
            meta={`FY-${year} · ${comparisonLabel} Forecast Scenario`}
            hiddenCols={hiddenCols}
            globalHiddenCols={globalPeriodHidden}
            filters={filters}
            wrapRef={(el) => { wrapRefs.current[1] = el; }} onScroll={(e) => handleScroll(1, e)} />
        </div>

        <div className={classes.tableSection}>
          <DataTable data={variance} isVariance={true} classes={classes} actualCols={actualCols} year={year}
            title="Variance"
            meta={`${liveLabel} Vs. ${comparisonLabel} Forecast`}
            hiddenCols={hiddenCols}
            globalHiddenCols={globalPeriodHidden}
            filters={filters}
            varianceSources={{ live, comparison, compareActualMonthKeys: comparisonActualCols }}
            wrapRef={(el) => { wrapRefs.current[2] = el; }} onScroll={(e) => handleScroll(2, e)} />
        </div>
      </div>
    </div>
  );
}
